import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useOperatorMode } from '../../lib/operatorMode.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  EmptyState,
  ErrorState,
  GaugeCard,
  KpiHeroBand,
  LoadingSkeleton,
  StatusChip,
  deriveKpiTone,
  toast,
} from '../../components/index.js';
import {
  CommandCenterTable,
  GuidedTourPanel,
  RapidStateStrip,
  ResetDialog,
  SeededBanner,
} from './RapidSharedChrome.jsx';
import './rapid.redesign.css';
import {
  clearRapidBrowserState,
  clearStaleRapidState,
  persistGlobalState,
  readGlobalState,
  readTourState,
  writeTourState,
} from './rapidPageState.js';
import { formatMoney, rapidHealthOrder, tourRouteForStep } from './rapidPageUtils.js';

export function RapidCommandCenterPage() {
  const rapidRes = useApi('/rapids');
  const resetMutation = useMutation();
  // AC-RAPID-2: Reset workspace, guided tour, and role-lens scripts are demo
  // chrome and render only inside operator mode (AC-SHELL-4).
  const { enabled: operatorModeEnabled } = useOperatorMode();
  const navigate = useNavigate();
  const [filterCategory, setFilterCategory] = useState('All');
  const [sortKey, setSortKey] = useState('priority');
  const [search, setSearch] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [globalState, setGlobalState] = useState(null);
  const [tourState, setTourState] = useState(readTourState());

  const payload = rapidRes.data?.data;
  usePageMetadata({
    title: 'RAPID Command Center',
    subtitle: 'Active RAPID portfolio view with one active RAPID and one routed candidate.',
  });

  useEffect(() => {
    if (!payload?.seedVersion) return;
    setGlobalState(readGlobalState(payload.seedVersion));
  }, [payload?.seedVersion]);

  useEffect(() => {
    if (!payload?.seedVersion) return;
    clearStaleRapidState(payload.seedVersion);
  }, [payload?.seedVersion]);

  const roleLens = globalState?.roleLens || 'Executive Sponsor';
  const modifiedLabel = globalState?.modified ? 'Modified' : 'Baseline (unchanged)';

  function updateRoleLens(nextRole) {
    if (!payload?.seedVersion) return;
    const next = persistGlobalState(payload.seedVersion, { ...globalState, roleLens: nextRole });
    setGlobalState(next);
  }

  function updateTour(next) {
    setTourState(next);
    writeTourState(next);
  }

  function goToTourStep(step) {
    const next = { open: true, step };
    updateTour(next);
    navigate(tourRouteForStep(step, 'RAPID-IMR-PHA-001'));
  }

  async function confirmReset() {
    try {
      await resetMutation.run(() =>
        api.post('/admin/seed/reset', { preserveRapidDemoFeedback: true }),
      );
      clearRapidBrowserState();
      if (payload?.seedVersion) setGlobalState(persistGlobalState(payload.seedVersion, {}));
      setTourState({ open: false, step: 1 });
      setFilterCategory('All');
      setSortKey('priority');
      setSearch('');
      setShowResetDialog(false);
      await rapidRes.refresh();
      toast.success('Workspace restored to baseline.');
    } catch (error) {
      toast.error(error.message || 'Reset failed');
    }
  }

  if (rapidRes.loading) {
    return <LoadingSkeleton rows={8} />;
  }
  if (rapidRes.error) return <ErrorState error={rapidRes.error} onRetry={rapidRes.refresh} />;
  if (!payload) return <EmptyState title="RAPID not available" />;
  if (!globalState) return <LoadingSkeleton rows={8} />;

  const filteredRapids = (payload.rapids || [])
    .filter((rapid) => filterCategory === 'All' || rapid.visibleCategory === filterCategory)
    .filter((rapid) => {
      if (!search.trim()) return true;
      const haystack =
        `${rapid.id} ${rapid.title} ${rapid.shortName} ${rapid.summary}`.toLowerCase();
      return haystack.includes(search.trim().toLowerCase());
    })
    .sort((left, right) => {
      if (sortKey === 'health') return rapidHealthOrder(left, right);
      return Number(left.priorityRank || 999) - Number(right.priorityRank || 999);
    });

  const allRapids = payload.rapids || [];
  const activeRapid =
    allRapids.find((rapid) => rapid.visibleCategory === 'Active') || allRapids[0] || null;
  const routedRapid =
    allRapids.find((rapid) => rapid.visibleCategory !== 'Active' && rapid.id !== activeRapid?.id) ||
    null;
  const activeScore = Number(activeRapid?.scoreTotal);
  const routedScore = Number(routedRapid?.scoreTotal);
  const activeTimeline = activeRapid?.timeline || {};
  const elapsed = Number(payload.summary.daysElapsed) || 0;
  const remaining = Number(payload.summary.daysRemaining) || 0;
  const totalDays = Number(payload.summary.totalClockDays) || elapsed + remaining || 0;
  const totalWeeks =
    Number(activeTimeline.totalWeeks) || (totalDays ? Math.round(totalDays / 7) : 6);
  const currentWeek =
    Number(activeTimeline.currentWeek) ||
    (totalDays
      ? Math.min(totalWeeks, Math.max(1, Math.ceil((elapsed / totalDays) * totalWeeks)))
      : 0);
  const clockPct = totalDays ? Math.round((elapsed / totalDays) * 100) : 0;

  const heroItems = [
    {
      id: 'primary-score',
      label: 'Primary RAPID score',
      value: Number.isFinite(activeScore) ? activeScore.toFixed(1) : '—',
      status: 'amber',
      statusLabel: 'Owner-confirmation items',
      footer: `${activeRapid?.id || ''} qualifies with owner-confirmation items`,
      icon: '🎯',
    },
    {
      id: 'routed-score',
      label: 'Routed candidate score',
      value: Number.isFinite(routedScore) ? routedScore.toFixed(1) : '—',
      status: 'slate',
      statusLabel: 'Routes elsewhere',
      footer: `${routedRapid?.id || 'Candidate'} routes elsewhere — matrix can say no`,
      icon: '↩',
    },
    {
      id: 'visible-clock',
      label: 'Visible clock',
      value: `${remaining}`,
      unit: 'days left',
      status: deriveKpiTone({ value: remaining, target: totalDays ? totalDays * 0.25 : remaining }),
      footer: `${elapsed} elapsed / ${remaining} remaining / ${totalDays} total days`,
      icon: '⏱',
    },
    {
      id: 'cost-avoidance',
      label: 'Cost avoidance',
      value: payload.summary.valueDisplay,
      status: 'blue',
      statusLabel: 'Estimated case',
      footer: `${formatMoney(payload.summary.costAvoidanceUsd)} estimated value case`,
      icon: '$',
    },
  ];

  const criteriaTone = Number.isFinite(activeScore)
    ? deriveKpiTone({ value: activeScore, target: 45 })
    : 'slate';
  const weekCells = Array.from({ length: totalWeeks || 0 }, (_, index) => {
    const weekNumber = index + 1;
    if (weekNumber < currentWeek) return 'is-elapsed';
    if (weekNumber === currentWeek) return 'is-current';
    return '';
  });

  return (
    <div data-testid="page-command-center">
      <SeededBanner banner={payload.banner} />
      {operatorModeEnabled ? (
        <>
          <RapidStateStrip
            modifiedLabel={modifiedLabel}
            onReset={() => setShowResetDialog(true)}
            onStartTour={() => goToTourStep(1)}
            tourOpen={tourState.open}
            roleLens={roleLens}
            roleViews={payload.roleViews}
            onRoleLensChange={updateRoleLens}
          />
          <GuidedTourPanel
            guidedTour={payload.guidedTour}
            tourState={tourState}
            onBack={() => goToTourStep(Math.max(1, tourState.step - 1))}
            onNext={() =>
              goToTourStep(Math.min((payload.guidedTour?.steps || []).length, tourState.step + 1))
            }
            onSkip={() => updateTour({ open: false, step: 1 })}
            onRestart={() => goToTourStep(1)}
          />
        </>
      ) : null}
      <section className="rapid-hero rapid-redesign-hero" data-testid="rapid-command-center">
        <div className="rapid-redesign-hero__intro">
          <div className="eyebrow">RAPID Command Center</div>
          <h1>RAPID Command Center</h1>
          <p>
            Current RAPID portfolio view across active and routed requests: the active flagship
            RAPID and the routed candidate that proves the matrix can say no.
          </p>
        </div>

        <KpiHeroBand eyebrow="Portfolio scoreboard" items={heroItems} columns={4} />

        <div className="rapid-redesign-boardrow">
          <div className="rapid-redesign-gauge-cell">
            <GaugeCard
              value={Number.isFinite(activeScore) ? Number(activeScore.toFixed(1)) : 0}
              min={0}
              max={90}
              label="Criteria score"
              sublabel={`Threshold 45 · ${activeRapid?.id || ''}`}
              tone={criteriaTone}
              deltaLabel={
                Number.isFinite(activeScore)
                  ? `${(activeScore - 45 >= 0 ? '+' : '') + (activeScore - 45).toFixed(0)} vs threshold`
                  : undefined
              }
              deltaTone={criteriaTone}
            />
          </div>

          <div className="card rapid-redesign-clock">
            <div className="rapid-redesign-clock__head">
              <span className="eyebrow">Six-week clock</span>
              <span className="rapid-redesign-clock__value">{remaining} days left</span>
            </div>
            <div className="score-bar" aria-hidden>
              <i
                className={deriveKpiTone({
                  value: remaining,
                  target: totalDays ? totalDays * 0.25 : remaining,
                })}
                style={{ width: `${clockPct}%` }}
              />
            </div>
            <div
              className="rapid-redesign-clock__weeks"
              role="img"
              aria-label={`Week ${currentWeek} of ${totalWeeks}; ${remaining} days remaining of ${totalDays}.`}
            >
              {weekCells.map((state, index) => (
                <span key={`week-${index + 1}`} className={`rapid-redesign-clock__week ${state}`} />
              ))}
            </div>
            <div className="rapid-redesign-clock__legend">
              <span>
                <i className="elapsed" />
                {elapsed} elapsed
              </span>
              <span>
                <i className="current" />
                Week {currentWeek} of {totalWeeks}
              </span>
              <span>
                <i className="remaining" />
                {remaining} remaining
              </span>
            </div>
          </div>
        </div>
      </section>
      <CommandCenterTable
        rapids={filteredRapids}
        totalRapids={Number(
          payload.pages?.rapids?.total ?? payload.summary.totalRecords ?? allRapids.length,
        )}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        sortKey={sortKey}
        setSortKey={setSortKey}
        search={search}
        setSearch={setSearch}
        onClear={() => {
          setFilterCategory('All');
          setSortKey('priority');
          setSearch('');
        }}
      />
      <section className="rapid-panel">
        <div className="rapid-section-heading">
          <div>
            <div className="eyebrow">Routed proof</div>
            <h2>Candidate preserved, not discarded</h2>
          </div>
          <span className="chip outline sm" data-testid="rapid-request-authoritative-total">
            {Number(payload.pages?.requests?.total ?? payload.requests?.length ?? 0)} total
          </span>
        </div>
        <div className="rapid-request-grid">
          {(payload.requests || []).map((request) => (
            <article className="rapid-request-card" key={request.id}>
              <strong>{request.title}</strong>
              <div className="muted small">{request.id}</div>
              <p>{request.intakeNotes}</p>
              <div className="rapid-chip-row">
                <StatusChip status={request.workflowState}>{request.workflowState}</StatusChip>
                <span className="chip outline sm">{request.routingOutcome}</span>
              </div>
              {/* AC-RAPID-7: routed-proof cards link to their intake-queue
                  record, and a request already linked to a RAPID links back. */}
              <div className="rapid-inline-actions">
                <Link
                  className="btn ghost sm"
                  data-testid={`request-intake-link-${request.id}`}
                  to={`/project-factory/requests/${request.id}`}
                >
                  View in intake queue
                </Link>
                {request.linkedRapidId ? (
                  <Link
                    className="btn ghost sm"
                    data-testid={`request-rapid-link-${request.id}`}
                    to={`/rapids/${request.linkedRapidId}`}
                  >
                    RAPID record {request.linkedRapidId}
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </section>
      <ResetDialog
        open={operatorModeEnabled && showResetDialog}
        resetting={resetMutation.running}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={confirmReset}
      />
    </div>
  );
}
